import { useState, useEffect, useRef } from 'react';
import { pollJobStatus, fetchReport } from '../lib/api';
import type { DiagnosisReport, JobStatus } from '../types';

interface PollState {
  status: JobStatus | 'idle';
  report: DiagnosisReport | null;
  error: string | null;
  elapsedSeconds: number;
}

const POLL_INTERVAL_MS = 3000;

export function useJobPoller(jobId: string | null): PollState {
  const [state, setState] = useState<PollState>({
    status: 'idle',
    report: null,
    error: null,
    elapsedSeconds: 0,
  });

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!jobId) return;

    startRef.current = Date.now();
    setState({ status: 'processing', report: null, error: null, elapsedSeconds: 0 });

    // Elapsed seconds ticker
    elapsedRef.current = setInterval(() => {
      setState((s) => ({ ...s, elapsedSeconds: Math.floor((Date.now() - startRef.current) / 1000) }));
    }, 1000);

    // Poll loop
    const poll = async () => {
      try {
        const { status, error } = await pollJobStatus(jobId);

        if (status === 'complete') {
          clearInterval(timerRef.current!);
          clearInterval(elapsedRef.current!);
          const { report } = await fetchReport(jobId);
          setState((s) => ({ ...s, status: 'complete', report, error: null }));
        } else if (status === 'failed') {
          clearInterval(timerRef.current!);
          clearInterval(elapsedRef.current!);
          setState((s) => ({ ...s, status: 'failed', error: error ?? 'Analysis failed.' }));
        } else {
          setState((s) => ({ ...s, status: 'processing' }));
        }
      } catch (err) {
        clearInterval(timerRef.current!);
        clearInterval(elapsedRef.current!);
        setState((s) => ({
          ...s,
          status: 'failed',
          error: err instanceof Error ? err.message : 'Network error.',
        }));
      }
    };

    poll(); // immediate first check
    timerRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      clearInterval(timerRef.current!);
      clearInterval(elapsedRef.current!);
    };
  }, [jobId]);

  return state;
}
